<template>
  <div>
    <div
      v-if="!loading"
      class="chord-diagram"
    >
      <div
        v-if="!empty"
      >
        <div class="level">
          <div class="level-left">
            <div class="field mb-0">
              <div class="heading">
                Filtrer par année
              </div>
              <div class="field has-addons">
                <p
                  v-for="date in dates"
                  :key="date.label"
                  class="control"
                  @click="updateFilters('dateRange', date.value)"
                >
                  <a
                    class="button is-small"
                    :class="{'is-dark': date.value[0] == filters.dateRange[0] && date.value[1] == filters.dateRange[1]}"
                  >
                    <span>{{ date.label }}</span>
                  </a>
                </p>
              </div>
            </div>

            <div
              v-if="filters.artist"
              class="field ml-30"
            >
              <div class="heading">
                Filtrer par rôle
              </div>
              <div class="field has-addons">
                <p
                  v-for="role in roles"
                  :key="role.value"
                  class="control"
                >
                  <a
                    class="button is-small"
                    :class="{'is-dark': filters.role == role.value}"
                    @click="updateFilters('role', role.value)"
                  >
                    <span>{{ role.label }}</span>
                  </a>
                </p>
              </div>
            </div>
          </div>
          <div class="level-right">
            <button
              v-if="help"
              class="button ml-30 mb-0 is-small is-light"
              @click="showInfo = true"
            >
              <span class="icon">
                <i class="mdi mdi-information-outline" />
              </span>
              <span>
                Comment lire le diagramme?
              </span>
            </button>
          </div>
        </div>

        <div class="columns">
          <div
            v-if="songList && highlightedSongs.length > 0"
            class="column"
          >
            <div
              class="left-panel"
              :class="{'is-tuto': activeSongsTutorial}"
            >
              <v-songs-list
                :songs="highlightedSongs"
                :title="songsTitle"
                :button="songListButton"
                :sort-on-parent="direction == 'target'"
                @closeButton="selectedFlux = null"
                @hover="highlightSong" 
              />
              <div class="tuto">
                <div class="box">
                  <p class="mb-15">
                    Chaque rangée présente une chanson d'origine à gauche ainsi qu'une adaptation directe à droite.
                  </p>
                  <button
                    class="button is-dark is-small"
                    @click="endTuto()"
                  >
                    Ok, j'ai compris
                  </button>
                </div>
                <img
                  src="../assets/img/pointer-top.svg"
                  alt=""
                  width="80"
                >
              </div>
            </div>
          </div>

          <div
            v-else
            class="column"
          >
            <br>
            <p>Les pays sont représentées par les segments du cercle avec une couleur unique. Les flux ont la même couleur que leur origine et la largeur indique leur taille. Un pays peut être à la fois l'origine ou la destination d'adaptations musicales. L'écart entre le flux et le segment d'un pays indique également la direction du flux: un petit écart indique l'origine; un grand écart indique la destination.</p>
            <br>
            <p>Le graphique circulaire présente les estimations des flux directionnels entre plus de 200 pays ayant produit de la musique. La fenêtre qui apparaît lorsque vous survolez la parcelle indique le nombre d'adaptations produites au pays (total entrant) et le nombre de chansons ayant été adaptées ailleurs (total sortant) du pays sur une période d'au moins 100 ans.</p>
            <br>
            <img
              src="../assets/img/help.png"
              width="350"
            >
          </div>

          <div class="column has-text-centered">
            <v-chord
              :matrix="matrix"
              :groups="groups"
              :highlight="highlight"
              :tuto="activeTutorial"
              @chordClick="chordClick"
              @endTuto="activeTutorial = false"
            />
          </div>
        </div>

        <b-modal :active.sync="showInfo">
          <div class="box has-text-left">
            <div class="columns">
              <div class="column is-narrow">
                <img
                  src="../assets/img/help.png"
                  width="250"
                >
              </div>
              <div class="column">
                <h2 class="title is-6">
                  Comment lire le diagramme?
                </h2>
                <p style="font-size: 0.9rem;">
                  Les pays sont représentées par les segments du cercle avec une couleur unique. Les flux ont la même couleur que leur origine et la largeur indique leur taille. Un pays peut être à la fois l'origine ou la destination d'adaptations musicales. L'écart entre le flux et le segment d'un pays indique également la direction du flux: un petit écart indique l'origine; un grand écart indique la destination.
                </p>
                <br>
                <p style="font-size: 0.9rem;">
                  Le graphique circulaire présente les estimations des flux directionnels entre plus de 200 pays ayant produit de la musique. La fenêtre qui apparaît lorsque vous survolez la parcelle indique le nombre d'adaptations produites au pays (total entrant) et le nombre de chansons ayant été adaptées ailleurs (total sortant) du pays sur une période d'au moins 100 ans.
                </p>
              </div>
            </div>
          </div>
        </b-modal>
      </div>
      <div v-else>
        Aucune donnée trouvée
      </div>
    </div>
    
    <div v-else>
      <loading-component />
    </div>
  </div>
</template>

<script>

import ChordGraph from "@/components/chord/ChordGraph";
import SongList from "@/components/SongList";
import ChordService from "@/services/chord.js";
import { min, max } from "d3";
import { deepCopy, setCookie, getCookie } from "@/services/helpers";

export default {
	components: {
		"v-chord": ChordGraph,
		"v-songs-list": SongList
	},
	props: {
		songs: {
			required: true,
			type: Array
		},
		songList: {
			default: true,
			type: Boolean
		},
		minDate: {
			type: Number
		},
		maxDate: {
			type: Number
		},
		artist: {
			default: null
		},
		direction: {
			default: "source"
		},
		help: {
			default: true,
			type: Boolean
		},
		tuto: {
			default: false,
			type: Boolean
		}
	},
	data () {
		return {
			loading: true,
			matrix: [],
			groups: [],
			filters: {
				dateRange: [],
				role: "artist",
				direction: this.direction,
				artist: this.artist
			},
			selectedFlux: null,
			highlight: null,
			empty: false,
			showInfo: false,
			activeTutorial: this.tuto,
			activeSongsTutorial: !this.hasSeenSongsTuto
		};
	},
	computed: {
		roles () {
			return [
				{label: "Tout", value: "artist"},
				{label: "Auteur", value: "author"},
				{label: "Interprète", value: "interpret"},
			];
		},
		minDate_ () {
			if (this.minDate !== undefined) {
				return this.minDate;
			}
			let minimum = min(this.songs.filter(s => s.parent_id != "" && s.country_origin != "").map(s => parseInt(s.year)));
			return Math.floor(minimum/10) * 10;
		},
		maxDate_ () {
			if (this.maxDate !== undefined) {
				return this.maxDate;
			}
			let maximum = max(this.songs.filter(s => s.parent_id != "" && s.country_origin != "").map(s => parseInt(s.year)));
			return Math.ceil(maximum/10) * 10;
		},
		dates () {
			let step = 50;

			if (this.maxDate_ - this.minDate_ <= 100) {
				step = 10;
			}

			let dates = [{
				label: "Tout",
				value: [this.minDate_, this.maxDate_]
			}];


			for (let i = this.minDate_; i < this.maxDate_; i += step) {
				dates.push({
					label: `${i}-${i+step}`,
					value: [i, i+step]
				});
			}

			return dates;
		},
		songsTitle () {
			return `${this.highlightedSongs.length} adaptations`;
		},
		songListButton () {
			if (this.selectedFlux !== null) {
				return `${this.selectedFlux.source} → ${this.selectedFlux.target}`;
			}
			return undefined;
		},
		highlightedSongs () {
            
			let validCountries = this.groups.filter(g => g.name != "Autres").map(c => c.name); // Remove "Autre" from array

			return this.chordService.filterSongs(this.filters).filter(song => {

				if (this.selectedFlux == null) {
					return true;
				}

				let songTargetOrigin = song.country_origin;
				let songOrigin = this.$store.state.songs[song.parent_id];
				let songSourceOrigin = songOrigin.country_origin;

				return (
					(validCountries.includes(songTargetOrigin) && songTargetOrigin == this.selectedFlux.target) || 
          (!validCountries.includes(songTargetOrigin) && this.selectedFlux.target == this.groups[this.groups.length - 1].name)
				) &&
        (
        	(validCountries.includes(songSourceOrigin) && songSourceOrigin == this.selectedFlux.source) ||
          (!validCountries.includes(songSourceOrigin) && this.selectedFlux.source == this.groups[this.groups.length - 1].name)
        );
			});
		},
		hasSeenSongsTuto() {
			return getCookie("songs-tuto") == "true" || false;
		}
	},
	mounted () {
		setTimeout(() => {
			this.resetFilters();

			this.chordService = new ChordService(this.songs);
			this.activeSongsTutorial = !this.hasSeenSongsTuto;

			try {
				this.updateChart();
				this.loading = false;
			} catch (e) {
				this.empty = true;
				this.loading = false;
			}
		}, 50);
	},
	methods: {
		updateChart () {
			let data = this.chordService.getChordData(this.filters);
			this.matrix = data.matrix;
			this.groups = data.groups;
		},
		chordClick (chord) {
			this.selectedFlux = {
				source: this.groups[chord.source.index].name,
				target: this.groups[chord.target.index].name
			};
		},
		updateFilters (filter, value) {
			let oldFilters = deepCopy(this.filters);

			this.selectedFlux = null;

			this.filters[filter] = value;

			try {
				this.updateChart();
				this.empty = false;
			} catch (e) {
				this.$buefy.snackbar.open({
					message: "Aucune donnée trouvée",
					type: "is-warning",
					queue: false
				});
                
				this.filters = oldFilters;
			}
		},
		resetFilters () {
			this.filters.dateRange = [this.minDate_, this.maxDate_];
			this.filters.artist = this.artist;
			this.filters.direction = this.direction;
		},
		highlightSong (data) {
			if (data !== null) {
				let validCountries = this.groups.filter(g => g.name != "Autres").map(c => c.name); // Remove "Autre" from array
				let source = validCountries.indexOf(data.target);
				if (source == -1) {
					source = this.groups.length - 1;
				}
				let target = validCountries.indexOf(data.source);
				if (target == -1) {
					target = this.groups.length - 1;
				}
				this.highlight = [source, target];
			} else {
				this.highlight = null;
			}
		},
		endTuto() {
			this.activeSongsTutorial = false;
			setCookie("songs-tuto", true, 10);
		}
	}
};
</script>
